define((require) => {
    "use strict";

    const _             = require("underscore");
    const $             = require("jquery");
    const Queue         = require("shared/services/sync/Queue");
    const Preconditions = require("shared/util/Preconditions");
    const Promise       = require("shared/util/Promise");

    // Safe guard to avoid most obvious queuing deadlocks...
    const MAX_NESTED_COUNT = 5;

    // Returns the (non-private) sync method name, e.g. "_save" -> "save" and "save" -> "save"...
    function syncName(method) {
        return method.charAt(0) === "_" ? method.substring(1) : method;
    }

    /**
     * @class {TargetQueue}
     * A <i>target queue</i> describes the queue for a given target entity over time regardless of sync method. It is
     * thus always a homogeneous queue with respect to target identity, though pending sync methods may be heterogeneous
     * over time. <p>
     *
     * A target queue can be blocked for one of two reasons: <ol>
     *
     * <li> It is used by a sequential queue that is busy with earlier requests in its queue, and the target queue will
     *      only be allowed to progress once the sequential queue has completed all prior queued sync methods; and/or
     *
     * <li> A sync method request is pending, e.g. on debounced save, and will not be allowed to be dequeued until
     *      the debounced timeout has been reached. Such pending sync method requests must still be queued, as they
     *      will affect the target in question, even though pending.
     *
     * </ol>
     *
     * A target queue will by default resolve to the target instance synced. <p>
     *
     * Implementation note: a target queue delegates the actual sync logic not related to queuing to a
     * <i>syncer</tt> - one type cannot really function without the other in a correct fashion.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     *
     * @see    Syncer
     * @see    SequentialQueue
     * @see    ParallelQueue
     */
    return Queue.extend("TargetQueue", {

        /**
         * @protected @type {Syncer}
         * The syncer wrapping the target synced by this queue; never falsey.
         */
        _syncer: null,
        /**
         * @protected @type {[]}
         * All identifiers used to globally identify the target synced by this queue. <p>
         *
         * The length may be larger than one on creation, e.g. the actual identifier not available until after
         * the model in question has been created. <p>
         *
         * The array is maintained by the sync manager using this target queue!
         *
         * @see QueueManager#_registerQueue
         * @see QueueManager#_unregisterQueue
         */
        _identifiers: null,

        /**
         * @constructor
         * Constructor, which creates this target queue.
         *
         * @param {SyncManager} manager        The sync manager that created this queue; never falsey.
         * @param {Syncer}      syncer         The syncer used to perform the actual sync for the target in question;
         *                                     never falsey.
         * @param {Function}   [resultHandler] Optional result handler to transform the result before resolved. If not
         *                                     supplied, the synced target will be resolved when this queue is empty.
         */
        constructor(manager, syncer, resultHandler) {
            this._syncer = syncer; // assign first, so it will be shown in toString representation from super constructor
            // Call super...
            this.__super(manager, resultHandler || (() => syncer.target));
            this._identifiers = [];
        },

        /**
         * Returns a new sync request based on the supplied sync options and (var-) arguments for the target used
         * by this syncer. <p>
         *
         * The returned request has the following format:
         * <pre>
         *   {
         *      method:   String,                       // The logical sync method, e.g. "save" (never private like "_save"),
         *      args:     [],                           // Arguments for <tt>invoke</tt>, if any.
         *      queue:    Queue,                        // The owning queue (this one).
         *      match:    Function(request) -> Boolean, // Returns true if this request matches the supplied request, false otherwise.
         *      validate: Function()-> *,               // Optional validate function, invoked with target scope, to
         *                                              // return null/undefined on success, otherwise the result will
         *                                              // be used as the error and the invocation will fail.
         *      invoke:   Function(args) -> *,          // Invokes the actual sync function on the target used by this
         *                                              // syncer with <tt>args</tt>, returning a promise or a value/raises
         *                                              // an error that will be wrapped in a promise
         *      cancelable: Function() -> Boolean       // Optional method to determine if the request is cancelable or
         *                                              // not, invoked with target scope. A sync request can always be
         *                                              // cancelled, but the actual (remote) work performed by a syncing
         *                                              // request cannot be cancelled in all cases, for example of
         *                                              // versioned updates. A cancelable sync request will thus ignore
         *                                              // a server response altogether and fail on cancel, while a non
         *                                              // cancelable request will silently update the target in the
         *                                              // background on complete, if ever, but will in the meantime be
         *                                              // tagged as failed.
         *      errorHandler: Function(error:*, values:*) -> Object, // optional error handler to be used as argument
         *                                                           // for ErrorHandler.fail.
         *      resultHandler: Function(object:*) -> Object,         // optional result handler to transform a result into a different
         *                                                           // one/type.
         *      blockedHandler: Function(queue:Queue) -> *           // optional blocked handler to return truthy while the request
         *                                                           // should (at least) be blocked, falsey otherwise.
         *   }
         * </pre>
         *
         * @param {String|*}  options The actual sync function to invoke, e.g. "save", or options like
         *                            <tt>{method:String, fn:Function, ..}</tt>; cannot be falsey.
         * @param {[]}        [args]  Args, as an array, for the actual sync function at invocation time. Will default
         *                            to empty array if falsey.
         *
         * @return {*} A new request as described; never null.
         *
         * @throws Error If no actual sync function is specified/can be deduced from the target.
         */
        request(options, args) {
            // Already a request?
            if (_.isFunction(options.invoke)) {
                return options; // request
            }

            const request = _.isString(options) ? {method: options} : _.extend({}, options);

            args = args || request.args || [];
            if (!_.isArray(args)) {
                args = [args];
            }

            let validate = request.validate;
            if (_.isBoolean(validate)) {
                const valid = validate ? null : "invalid";
                validate = () => valid;
            } else if (!_.isFunction(validate)) {
                validate = null;
            }

            const queue      = this;
            const syncer     = queue._syncer;
            const target     = syncer.target;
            const syncState  = target.syncState;
            const cancelable = (_.isFunction(request.cancelable) && request.cancelable) || (() => true);
            const http       = typeof request.http === "undefined" ? true : request.http;

            let deferred = null; // lazily created deferred backing this request, or inherited on nested requests!
            let name     = syncName(request.method); // skip private method names, e.g. "_save" -> "save"
            let fn       = request.fn; // actual sync function to invoke

            // On nested sync requests, we use the public deferred for the top level already active sync request, which
            // will be resolved once the nested sync requests completes...
            if (syncer._nested) {
                // Bail out early...
                Preconditions.checkState(syncer._nested <= MAX_NESTED_COUNT, new Error(`Max nested count exceeded: ${MAX_NESTED_COUNT}`));
                deferred = queue.active().request.deferred(); // this.active() never null on nested!
            }

            // Lookup (private) sync method if none is specified...
            if (!_.isFunction(fn)) {
                if (fn) { // === request.fn
                    fn = function() {
                        // Http short cut?
                        if (http) {
                            let data = request.fn.data;
                            // Add json data but only if not already present to avoid superfluous marshalling. We cannot
                            // marshall before this point, i.e. sync time, since "target" and/or "data" may have changed
                            // since enqueue time, e.g. gotten a new version because of previous completed sync requests...
                            if (request.fn.dataType === "json") {
                                if (_.isFunction(data)) { // e.g. context defined callback
                                    data = data(request);
                                } else if (!data && _.isFunction(target.toJSON)) { // e.g. models...
                                    data = target.toJSON(options);
                                }
                                // GET requests already handled via ajax, but not others like POST...
                                if (request.fn.type && request.fn.type !== "GET" && !_.isString(data)) {
                                    data = JSON.stringify(data); // stringify(undefined) is undefined
                                }
                                request.fn.data = data;
                            }
                            return syncer.cancelable($.ajax(request.fn), request);
                        }
                        return request.fn;
                    };
                } else {
                    fn = target[request.method]; // can be private!
                }
            }

            // We must have a sync function by now, or we must raise an error - this is indeed a programmer error,
            // so do not continue if so...
            Preconditions.checkState(_.isFunction(fn), new Error(`No sync method: <${request.method}>`));

            // Look up configured sync:state aliases, like "save" -> "share" for better notifications, e.g. we
            // can write stuff like ".. the share of ..." instead of ".. the save of .." to denote a sharing business
            // logic while we are in fact saving (a share, in this case)...
            name = (syncState && syncState.alias[name]) || name;

            // Create and return request, remembering context specific request properties...
            return _.extend(request, {
                method:        name,
                args,
                queue,
                validate,
                cancelable,
                http,

                errorHandler:   _.isFunction(request.errorHandler)   ? request.errorHandler.bind(target) : null, // callback to ErrorHandler, so must be bound explicitly
                undoHandler:    _.isFunction(request.undoHandler)    ? request.undoHandler.bind(target)  : null,
                resultHandler:  _.isFunction(request.resultHandler)  ? request.resultHandler             : null,
                blockedHandler: _.isFunction(request.blockedHandler) ? request.blockedHandler            : (() => false),

                deferred() {
                    if (!deferred) {
                        deferred = syncer._deferred(request);
                    }
                    return deferred;
                },
                invoke() {
                    return fn.apply(target, args);
                },
                match(r) {
                    return r && name === r.method && _.isEqual(args, r.args);
                },
                toString: () => `${queue.id}:${queue.size()}[${name}]`
            });
        },

        /**
         * @private
         * Checks this queue to see if a corresponding request is already enqueued, returning the entry in question
         * if so. The is done via <tt>request.match(..)</tt>. <p>
         *
         * We allow any immutable matching queued request to be reused as long as we do not have mutable requests
         * after it; if so, we want the changes made by said mutable request to be reflected in the current immutable
         * queued request.
         *
         * @param {*} request The request to lookup, based on method name and arguments; never null.
         * @return {{i:Number, type:String, entry:*}} The meta data for enqueued entry for <tt>request</tt> for this
         *         queue, if any; or null if not found (or not applicable).
         */
        _enqueued(request) {
            let n = this._queue.length;
            let i = n--;
            let entry;

            while (i--) {
                entry = this._queue[i];
                if (request.match(entry.request)) {
                    // "tail queued" allows us to drop successive mutable queued requests with the same sync method...
                    return {i, type: i === n ? "tail queued" : "queued", entry};
                }
                // We have already covered the special case for allowing mutable requests at the end of the queue,
                // but if here and not immutable, we cannot reuse existing queued one as we have mutable ones
                // for this very target, and we want to make sure said changes are reflect on sync. That is, a "fetch",
                // then a "save", then a "fetch" again should make sure the latter "fetch" gets relevant (server)
                // changes from the intermediate save...
                if (!entry.request.immutable) {
                    return null;
                }
            }

            // Final check for immutable requests: check if the active one matches us and use it if so...
            if (request.immutable) {
                entry = this.active();
                if (entry && request.match(entry.request)) {
                    return {i: -1, type: "active", entry};
                }
            }
            return null;
        },

        _enqueue(enqueue, options, args, blockingQueue) {
            // Create an actual sync request...
            const queue = this;
            const request = queue.request(options, args);
            const immutable = queue._syncer.syncState().immutable;

            // Silently drop mutable requests for an immutable target...
            if (typeof immutable !== "undefined" && immutable && !request.immutable) {
                queue._log.verbose(`Dropping mutable sync request for immutable target: ${request} -> ${queue}`, request, queue);
                return null;
            }

            const nested = queue._syncer._nested;

            let entry  = !nested && queue._enqueued(request);

            const delay  = _.isNumber(options.delay) ? options.delay : null; // for debounced queuing, e.g. only "save" after no queuing activity in "delay" ms...

            // Make sure we do not have an existing (immutable or tailed) queue entry with a matching sync method; if
            // so, we do not queue a new request, and simply return an existing promise for contexts to use (i.e. for
            // an earlier immutable queued request). If no queue, but an active immutable one, e.g. "fetch", we can drop
            // the enqueuing as well. On nested enqueuing with the *same* sync method, we cannot drop the duplicate
            // request as that will typically be doing the actual work...
            if (entry && (request.immutable || entry.type.indexOf("tail") !== -1)) {
                queue._log.verbose(`Dropping sync request, equivalent already ${entry.type}: ${request} -> ${queue}`, request, entry, queue);
                // Use actual queue entry, not the meta data about it...
                entry = entry.entry;
                // If already pending, make sure we are still pending within the original specified delay...
                if (entry._pending) {
                    entry._pending();
                }
                entry._addBlocker(blockingQueue);
                // Earlier given out promise we will reuse...
                return entry.promise();
            }

            // Obtain the public deferred from the sync request from which public promises are handed out. On
            // nested enqueuing, the deferred is actually that of the top level, not active, sync request. This
            // also means we will get a promise backed by the deferred from the top level request...
            const deferred = request.deferred();
            let promise; // initialised below, when we have an entry to tag it with!

            // The syncer will internally use its own promise during invocation, i.e. the one returned by the
            // actual sync method or a fresh one created to wrap the result/error from said method. We therefore
            // obtain a freshly created deferred here and expose promises from it, not the internal promise from
            // the syncer. When the internal promise from the syncer completes (from the sync method execution),
            // we signal the exposed promises accordingly (those from the deferred obtained here). We have to do
            // it like this to ensure entries queued by this queue can hand out a promise the context can be
            // notified by once the specific queued request completes, and to handle timeout for sync method
            // promises...
            entry = {
                request, // to check for duplicates and for cancellation based on sync method name
                _queue:   [],      // to handle blocking, public so the sequential queue can remove it self as the blocker
                _pending: null,

                _addBlocker(queue) {
                    if (queue) {
                        const i = entry._queue.indexOf(queue);
                        if (i === -1) {
                            entry._queue.push(queue);
                        }
                    }
                },
                removeBlocker(queue) { // public, since sequential queue will call it!
                    const i = entry._queue.indexOf(queue);
                    if (i !== -1) {
                        entry._queue.splice(i, 1);
                    }
                },

                // can be blocked by a sequential queue or because pending or because of a user defined blockade
                blocked() {
                    if (entry._queue.length) {
                        return entry._queue[0].id || "queue"; // blocked by at least one queue, use the name of the first one
                    } else if (entry._pending) {
                        return "pending"; // blocked during pending timeout
                    }
                    return request.blockedHandler(queue); // context specific
                },
                promise() { return promise; },
                sync() {
                    // Sync...
                    queue._syncer.invoke(request);

                    // On creation, for example, the target identifier can change after sync, so we must make
                    // sure we use the same queue for logically equivalent, but different, identifiers, or
                    // we will end up using different target queues for the same target instance. We have to
                    // use a "done" handler here, to ensure any context defined handlers will fire first, e.g.
                    // model updates changing the identifier, before we try to check the new identifier...
                    promise.done(() => {
                        queue.manager._registerQueue(queue);
                    });

                    return promise;
                },
                cancel(silent) {
                    if (entry._pending) {
                        delete entry._pending;
                    }
                    // Cancelling the deferred for *this* entry will reject all given out promises!...
                    return queue._syncer.cancel(request, silent);
                },
                toString: () => request.toString()
            };

            // Register blocking (sequential) queue, if any...
            entry._addBlocker(blockingQueue);

            // Create promise that is returned by the entry just created, unless we are nested and are using an
            // earlier obtained promise...
            promise = Promise.create(deferred, {entry});

            // Create debounced function to handle pending functionality...
            if (delay) {
                entry._pending = _.debounce(() => {
                    // This callback can be called sometime in the future, perhaps even after the queue has been
                    // stopped, including if cancelled, so we have to take this into account. On cancel, we will
                    // already have deleted "pending", so when this fires, we know we have been cancelled if no
                    // such property...
                    if (!queue._stop && entry._pending) {
                        // No longer pending...
                        delete entry._pending;
                        // Continue with syncing this queue - can still be blocked, though, either by this entry
                        // or some other entry...
                        queue.sync(); // will be ignored if already syncing another entry
                    }
                }, delay);

                // Make sure we trigger "sync:pending" state for the target unless a sync is already in progress;
                // that is, we only trigger a pending event if the sync state is currently complete, or it would
                // mess with the current execution flow (handled by the syncer)...
                queue._syncer._pending(request);

                // Call it to ensure we will dequeue even if no more pending queue requests arrive...!
                entry._pending();
            }

            //
            // If we have a nested count > 0, it means that this syncer while invoked is trying to enqueue
            // another request with what ever sync method for the same target - which can cause a deadlock
            // unless we handle said scenario explicitly. This scenario is typical if for example some external
            // context uses the sync manager to operate on some targets, invoking target methods that uses the
            // sync manager as well (for the same target(s), typically on the same sync method (name)). If so,
            // the nested sync method will typically return a queue promise, since that is what it does if called
            // normally (non tested) as well, but since nested in this case, the request cannot be dequeued before
            // the outer one (where nested = 0) is complete - but it will depend on the promise returned by the
            // nested enqueuing; hence, deadlock! For nested methods that do not return queue promises, this is
            // not the case; for normal promises, yes, but they will eventually be resolved/rejected when the
            // actual sync work is done or has timed out. Also, since the top level (non nested) request has
            // already given out promises, they should resolve only when the nested sync request has completed
            // and not before. This is why we have to handle this scenario at queue time, and not after the sync
            // request has been invoked (i.e. the one that does the nested queuing).
            //
            // Confused? You won't be after the this episode of SOAP!... :)
            //

            if (nested) { // TODO: what happens on cancel of nested sync? We do not want to sync here if already cancelled!
                queue._log.verbose(`Nested sync depth #${nested}: ${entry} (active: ${queue.active()}) -> ${queue}`, entry);
                queue._sync(entry); // will return false when blocked...
                return promise; // ...so always hand out the valid promise!
            }

            // Not nested, so lets enqueue for later syncing...
            return enqueue(queue._syncer.target, () => entry);
        },

        _result(results) {
            return results[0]; // synced target
        },

        /**
         * Cancels the supplied entry and all entries queued after in in this target queue. <p>
         *
         * A "sync:" + method + ":cancel" event will only be triggered if <tt>entry</tt> is the active queue entry
         * or the first queued one (i.e. a pending one). If not, cancelled queue entries will just be dropped
         * silently, though their given out promises will of course be rejected with a value of <tt>CANCELLED</tt>.
         *
         * @param  {*} entry The queue entry, from this target queue, to cancel; cannot be falsey.
         * @return {Boolean} True if <tt>entry</tt>, and possible other entries, are cancelled, false otherwise.
         *
         * @see    #cancelMethod
         * @see    #cancel
         */
        cancelEntry(entry) {
            // Are we cancelling the active or first queued entry? If so, we are cancelling the entire queue...
            const i = this._queue.indexOf(entry);
            if (entry === this._active || i === 0) {
                return this.cancel();

            // Cancel entry and all after it, in a silent manner that will not trigger a sync:state change since
            // that depends on an earlier queued entry...
            } else if (i !== -1) {
                while (this._queue.length > i) {
                    this._queue[i].cancel(true); // silent, must not notify about change target sync state
                    this._queue.splice(i, 1);
                }
                return true;
            }
            return false;
        },

        /**
         * Finds the first active or queued sync request having the specified sync method, and cancels it and all
         * following queued entries, regardless of their sync method. <p>
         *
         * Sync requests queued before the first such found entry are not affected by this. <p>
         *
         * A "sync:" + method + ":cancel" event will only be triggered if the active queue entry or the first queued
         * one (i.e. a pending one) is cancelled by this. If not, cancelled queue entries will just be dropped
         * silently, though their given out promises will of course be rejected with a value of <tt>CANCELLED</tt> (also
         * for the active or first entry if they are cancelled).
         *
         * @param {String} method The sync method to find the first active or queued entry for, which will be
         *                        cancelled along side all following queue entries, regardless of sync method; cannot
         *                        be falsey.
         * @return {Boolean} True if at least one entry was cancelled, false otherwise.
         *
         * @see    #cancelEntry
         * @see    #cancel
         */
        cancelMethod(method) {
            // Look up first entry, including the active one, with the specified sync method and cancel it and
            // all following sync requests, if any...

            // Active?
            if (this._active && this._active.request.method === method) {
                return this.cancelEntry(this._active);
            }

            // Queue?
            let i;
            let entry;

            for (i = 0; i < this._queue.length; i++) {
                entry = this._queue[i];
                if (entry.request.method === method) {
                    return this.cancelEntry(entry);
                }
            }

            // None!
            return false;
        },

        /**
         * Returns the string representation of this queue.
         *
         * @return {String} The string representation of this queue; never falsey.
         */
        toString() {
            return `${this.__typeName}[id: ${this.id}; syncer: ${this._syncer}; size: ${this.size()}; age: ${((this._stop || Date.now()) - this._start)}ms]`;
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/services/sync/TargetQueue.js